import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  FaTimes, FaChevronDown, FaChevronUp, FaFile, FaTrash,
  FaSave, FaSpinner, FaEye, FaUpload, FaClock, FaCheck
} from "react-icons/fa";
import FilePreviewModal from "./FilePreviewModal";

const MAX_FILES_PER_QUESTION = 5;
const MAX_SIZE_PER_QUESTION = 50 * 1024 * 1024; // 50MB
const MAX_BOTTOM_FILES = 10;
const MAX_BOTTOM_SIZE = 100 * 1024 * 1024; // 100MB

const Step1ChecklistModal = ({ show, onClose, userId, memberIndex, onSaved }) => {
  const [answers, setAnswers] = useState({});
  const [comments, setComments] = useState({});
  const [expanded, setExpanded] = useState({});
  // Per-question files: { [questionKey]: [{ file, name, size, type, url, isStored }] }
  const [questionFiles, setQuestionFiles] = useState({});
  const [bottomFiles, setBottomFiles] = useState([]);
  const [formQuestions, setFormQuestions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [assignedForm, setAssignedForm] = useState('');
  const [userName, setUserName] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);
  const [dragOverQuestion, setDragOverQuestion] = useState(null);
  const [dragOverBottom, setDragOverBottom] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  const modalRoot = typeof document !== 'undefined' ? document.body : null;

  useEffect(() => {
    if (!show || !userId) return;

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem("employeeToken");

        let url = `${process.env.REACT_APP_API_BASE_URL}/api/auth/step1-checklist/${userId}`;
        if (memberIndex !== undefined && memberIndex !== null && memberIndex >= 0) {
          url += `/${memberIndex}`;
        }

        const step1Response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!step1Response.ok) throw new Error('Failed to fetch step 1 data');
        const step1Data = await step1Response.json();

        setAssignedForm(step1Data.assignedForm || '1040');
        setUserName(step1Data.userName || '');
        setIsSubmitted(step1Data.step1Data?.status === 'submitted');
        setSubmittedAt(step1Data.step1Data?.submittedAt || null);

        const rawAnswers = step1Data.step1Data?.answers || {};
        const rawComments = step1Data.step1Data?.comments || {};
        const parsedAnswers = rawAnswers instanceof Map ? Object.fromEntries(rawAnswers) : (typeof rawAnswers === 'object' ? rawAnswers : {});
        const parsedComments = rawComments instanceof Map ? Object.fromEntries(rawComments) : (typeof rawComments === 'object' ? rawComments : {});

        setAnswers(parsedAnswers);
        setComments(parsedComments);

        // Restore per-question files from stored files
        const storedFiles = step1Data.step1Data?.files || [];
        const restoredQuestionFiles = {};
        storedFiles.forEach(file => {
          const key = file.questionKey;
          if (key && key !== '_bottom') {
            if (!restoredQuestionFiles[key]) restoredQuestionFiles[key] = [];
            restoredQuestionFiles[key].push({
              name: file.name,
              size: file.size,
              type: file.type,
              url: file.url,
              isStored: true,
              uploadedBy: file.uploadedBy || 'user'
            });
          }
        });
        setQuestionFiles(restoredQuestionFiles);

        // Restore bottom files
        const restoredBottom = storedFiles
          .filter(f => f.questionKey === '_bottom')
          .map(f => ({
            name: f.name,
            size: f.size,
            type: f.type,
            url: f.url,
            isStored: true,
            uploadedBy: f.uploadedBy || 'user'
          }));
        setBottomFiles(restoredBottom);

        const formType = step1Data.assignedForm || '1040';
        const questionsResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/questions/form/${formType}`);
        if (questionsResponse.ok) {
          const questionsData = await questionsResponse.json();
          setFormQuestions(questionsData.data || {});
        } else {
          setFormQuestions(getFallbackQuestions(formType));
        }
      } catch (err) {
        console.error('Error fetching checklist data:', err);
        setError('Failed to load checklist data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [show, userId, memberIndex]);

  useEffect(() => {
    if (!show || !modalRoot) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [show, modalRoot]);

  const getFallbackQuestions = (formType) => {
    const fallback = {
      "1040": {
        "Document Upload by Client": [
          "Prior-year tax return copy (for reference)",
          "W-2 forms from all employers",
          "1099-NEC (Self-employment income)",
          "1099-K (Payments from platforms like PayPal, Stripe, etc.)",
          "1099-INT (Interest income)",
          "1099-DIV (Dividends received)",
          "1099-B (Stocks, bonds, or cryptocurrency sales)",
          "Social Security income (SSA-1099)",
          "1099-R (Retirement distributions)",
          "1099-G (Unemployment benefits)",
          "W-2G (Gambling winnings)",
          "1098-E (Student loan interest)",
          "5498 (IRA contributions)",
          "5498-SA (HSA contributions)",
          "1098 (Mortgage interest)",
          "Education credit (1098-T)",
          "Form K1 document If any",
          "Business income & expense records",
          "Rental income & expenses"
        ],
        "Basic Information of Client": [
          "Taxpayer's full name",
          "Taxpayer's SSN",
          "Taxpayer's Date of Birth",
          "Spouse's full name(if applicable)",
          "Spouse's SSN",
          "Spouse's Date of Birth",
          "Current mailing address",
          "Contact details",
          "Filing status (Single, MFJ, MFS, HOH, Qualifying Widow(er))",
          "Dependents' names SSNs, and dates of birth",
          "Dependents SSN",
          "Dependents date of birth",
          "Bank account details for direct deposit:-",
          "Do you want your refund directly deposited?",
          "Do you want your tax balance directly drafted?",
          "Bank Name",
          "Routing Number (9 Digits).",
          "Account Number",
          "Type of Account-Checking or Savings",
          "Quarterly estimated tax payments made",
          "Is you traded in any virtual currency in 2024?"
        ],
        "Are you having any foreign financials accounts (outside USA)?": [
          "FBAR/Form 8938 (Foreign accounts & assets)",
          "Asset Type",
          "Individual/ Joint Ownership",
          "Account Number",
          "Name of Financial Institution/Corporation",
          "Complete Address of Financial Institution/Corporation",
          "Maximum Foreign Currency Value During 2024",
          "Foreign Currency Value as of 12/31/2024",
          "Foreign Currency Unit",
          "Exchange Rate",
          "Form 8621 Mutual Fund or Any Passive Funds Hold outside USA",
          "Maximum Foreign Currency Value During 2024",
          "Foreign Currency Value as of 12/31/2024",
          "Foreign Currency Unit",
          "Exchange Rate",
          "Were you a signatory over any foreign financial accounts?",
          "Did you receive any foreign gifts or inheritances over $100,000?",
          "Do you have any foreign trusts?",
          "Did you have any foreign real estate transactions?",
          "Any other foreign income or assets not listed above?",
          "Comments/Additional Information"
        ]
      }
    };
    return fallback[formType] || fallback["1040"];
  };

  const totalQuestions = Object.values(formQuestions).reduce(
    (total, section) => total + section.length, 0
  );

  const calculateProgress = () => {
    return Object.keys(answers).filter((k) => answers[k] !== undefined && answers[k] !== '').length;
  };

  const calculateSectionProgress = (section) => {
    if (!formQuestions[section]) return 0;
    return formQuestions[section].filter((_, index) => answers[`${section}-${index}`] !== undefined && answers[`${section}-${index}`] !== '').length;
  };

  const formatDateTime = (value) => {
    if (!value) return null;
    try {
      const date = new Date(value);
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    } catch (err) {
      return value;
    }
  };

  const handleAnswer = (index, value) => {
    if (!isSubmitted) {
      setAnswers((prev) => {
        const current = prev[index] || "";
        let next = value;
        
        if (value === "yes" || value === "comment") {
          // If we're picking yes/comment, we must clear no/na
          if (current === "no" || current === "na") {
            next = value;
          } else {
            const parts = current.split(",").filter(p => p === "yes" || p === "comment");
            if (parts.includes(value)) {
              // Toggle off if it's already there
              next = parts.filter(p => p !== value).join(",");
            } else {
              // Add to selected parts
              next = [...parts, value].sort().reverse().join(","); // "yes,comment" or "yes" or "comment"
            }
          }
        } else {
          // "no" or "na" clears everything else
          next = value;
        }
        return { ...prev, [index]: next };
      });
    }
  };

  const handleComment = (index, value) => {
    if (!isSubmitted) {
      setComments((prev) => ({ ...prev, [index]: value }));
    }
  };

  const toggleQuestion = (question) => {
    setExpanded((prev) => ({ ...prev, [question]: !prev[question] }));
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getQuestionFilesTotalSize = (questionKey) => {
    return (questionFiles[questionKey] || []).reduce((sum, f) => sum + (f.size || 0), 0);
  };

  const getBottomFilesTotalSize = () => {
    return (bottomFiles || []).reduce((sum, f) => sum + (f.size || 0), 0);
  };

  // ---- Per-question file handlers (Part A only) ----
  const handleQuestionFileChange = (e, questionKey) => {
    if (isSubmitted) return;
    const newFiles = Array.from(e.target.files);
    if (!newFiles.length) return;

    const currentTotalSize = getQuestionFilesTotalSize(questionKey);
    const incomingSize = newFiles.reduce((sum, f) => sum + f.size, 0);

    if (currentTotalSize + incomingSize > MAX_SIZE_PER_QUESTION) {
      alert(`The total size for this question cannot exceed 50MB. Current: ${formatFileSize(currentTotalSize)}, New: ${formatFileSize(incomingSize)}`);
      return;
    }

    setQuestionFiles(prev => {
      const existing = prev[questionKey] || [];
      const available = MAX_FILES_PER_QUESTION - existing.length;
      if (available <= 0) {
        alert(`You can only upload up to ${MAX_FILES_PER_QUESTION} files per question.`);
        return prev;
      }
      const toAdd = newFiles.slice(0, available);
      if (newFiles.length > available) {
        alert(`Only ${available} file(s) can be added. Max ${MAX_FILES_PER_QUESTION} per question.`);
      }
      const formatted = toAdd.map(file => ({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        progress: 0,
        uploadedBy: 'admin'
      }));
      return { ...prev, [questionKey]: [...existing, ...formatted] };
    });
    e.target.value = '';
  };

  const removeQuestionFile = (questionKey, fileIndex) => {
    if (isSubmitted) return;
    if (window.confirm("Are you sure you want to remove this document?")) {
      setQuestionFiles(prev => {
        const updated = (prev[questionKey] || []).filter((_, i) => i !== fileIndex);
        return { ...prev, [questionKey]: updated };
      });
    }
  };

  const handleBottomFileChange = (e) => {
    if (isSubmitted) return;
    const newFiles = Array.from(e.target.files);
    if (!newFiles.length) return;

    const currentTotalSize = getBottomFilesTotalSize();
    const incomingSize = newFiles.reduce((sum, f) => sum + f.size, 0);

    if (currentTotalSize + incomingSize > MAX_BOTTOM_SIZE) {
      alert(`The total size for additional files cannot exceed 100MB. Current: ${formatFileSize(currentTotalSize)}, New: ${formatFileSize(incomingSize)}`);
      return;
    }

    if (bottomFiles.length + newFiles.length > MAX_BOTTOM_FILES) {
      alert(`You can only upload up to ${MAX_BOTTOM_FILES} additional files.`);
      return;
    }

    const formatted = newFiles.map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      isStored: false,
      uploadedBy: 'admin'
    }));

    setBottomFiles([...bottomFiles, ...formatted]);
    e.target.value = '';
  };

  const removeBottomFile = (index) => {
    if (isSubmitted) return;
    if (window.confirm("Are you sure you want to remove this document?")) {
      setBottomFiles(bottomFiles.filter((_, i) => i !== index));
    }
  };

  const renderBottomFiles = () => {
    if (!bottomFiles || bottomFiles.length === 0) return null;
    return (
      <div className="mt-3 space-y-1.5">
        {bottomFiles.map((file, fi) => (
          <div key={fi} className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-600">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <FaFile className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-900 dark:text-white truncate font-medium" title={file.name}>{file.name}</p>
                <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 ml-2">
              <button
                onClick={() => setPreviewFile(file)}
                className="p-1 text-blue-500 hover:text-blue-600 transition-colors flex-shrink-0"
                title="Preview file"
              >
                <FaEye className="w-3.5 h-3.5" />
              </button>
              {!isSubmitted && (
                <button
                  onClick={() => removeBottomFile(fi)}
                  className="p-1 text-red-500 hover:text-red-600 transition-colors flex-shrink-0"
                  title="Remove file"
                >
                  <FaTrash className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const buildRenamedFileName = (originalName, partLetter, questionNumber) => {
    const lastDot = originalName.lastIndexOf('.');
    if (lastDot === -1) return `${originalName}_${partLetter}_Q${questionNumber}`;
    const base = originalName.substring(0, lastDot);
    const ext = originalName.substring(lastDot);
    return `${base}_${partLetter}_Q${questionNumber}${ext}`;
  };

  const handleSave = async () => {
    if (isSubmitted) return;
    setSaving(true);

    try {
      const token = localStorage.getItem("employeeToken");
      const sectionKeys = Object.keys(formQuestions);
      const allFileRecords = [];

      // Upload new per-question files
      const currentQuestionFiles = { ...questionFiles };
      for (const [questionKey, files] of Object.entries(currentQuestionFiles)) {
        const newFiles = files.filter(f => f.file && !f.isStored);
        if (newFiles.length === 0) continue;

        const [sectionName, subIndexStr] = questionKey.split(/-(?=\d+$)/);
        const subIndex = parseInt(subIndexStr, 10);
        const sectionIdx = sectionKeys.indexOf(sectionName);
        const partLetter = `${String.fromCharCode(65 + sectionIdx)}-${sectionIdx + 1}`;

        const formData = new FormData();
        newFiles.forEach(f => {
          const renamed = buildRenamedFileName(f.name, partLetter, subIndex + 1);
          const renamedFile = new File([f.file], renamed, { type: f.type });
          formData.append('files', renamedFile);
        });

        // Upload via admin-specific endpoint (does NOT require admin to be in User collection)
        const uploadResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/admin-upload-checklist-files`, {
          method: "POST",
          body: formData,
          headers: { Authorization: `Bearer ${token}` },
        });

        if (uploadResponse.ok) {
          const uploadResult = await uploadResponse.json();
          const fileUrls = uploadResult.fileUrls || [];
          
          const updatedFilesForQuestion = [...files];
          updatedFilesForQuestion.forEach((f, fi) => {
            if (f.file && !f.isStored) {
              const renamed = buildRenamedFileName(f.name, partLetter, subIndex + 1);
              const match = fileUrls.find(u => typeof u === 'object' ? (u.originalName === renamed || u.originalName === f.name) : (u.includes(encodeURIComponent(renamed)) || u.includes(encodeURIComponent(f.name))));
              
              updatedFilesForQuestion[fi] = {
                ...f,
                url: typeof match === 'object' ? match.fileUrl : (match || ''),
                isStored: true,
                file: null
              };
            }
          });
          currentQuestionFiles[questionKey] = updatedFilesForQuestion;
        } else {
          console.error('File upload failed for question:', questionKey);
          throw new Error('Failed to upload files. Please try again.');
        }
      }

      setQuestionFiles(currentQuestionFiles);

      // Upload new bottom files
      let currentBottomFiles = [...bottomFiles];
      const newBottomFiles = currentBottomFiles.filter(f => f.file && !f.isStored);
      if (newBottomFiles.length > 0) {
        const bottomFormData = new FormData();
        newBottomFiles.forEach(f => {
          bottomFormData.append('files', new File([f.file], f.name, { type: f.type }));
        });
        const bottomUploadResp = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/admin-upload-checklist-files`, {
          method: "POST",
          body: bottomFormData,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (bottomUploadResp.ok) {
          const bottomResult = await bottomUploadResp.json();
          const bottomUrls = bottomResult.fileUrls || [];
          currentBottomFiles = currentBottomFiles.map(f => {
            if (f.file && !f.isStored) {
              const match = bottomUrls.find(u => typeof u === 'object' ? (u.originalName === f.name) : (u.includes(encodeURIComponent(f.name))));
              return { ...f, url: typeof match === 'object' ? match.fileUrl : (match || ''), isStored: true, file: null };
            }
            return f;
          });
          setBottomFiles(currentBottomFiles);
        } else {
          console.error('Bottom file upload failed');
          throw new Error('Failed to upload additional files. Please try again.');
        }
      }

      // 3. Prepare final files array for the database
      // Filter out '_bottom' key if it exists in currentQuestionFiles (it shouldn't if restoration is fixed, but let's be safe)
      Object.keys(currentQuestionFiles).filter(k => k !== '_bottom').forEach(questionKey => {
        const files = currentQuestionFiles[questionKey] || [];
        files.forEach(f => {
          if (f.url) {
            allFileRecords.push({
              questionKey,
              name: f.name,
              size: f.size,
              type: f.type,
              url: f.url,
              renamedName: f.renamedName || f.name,
              uploadedBy: f.uploadedBy || 'admin'
            });
          }
        });
      });

      // Add bottom files to records
      currentBottomFiles.forEach(f => {
        if (f.url) {
          allFileRecords.push({
            questionKey: '_bottom',
            name: f.name,
            size: f.size,
            type: f.type,
            url: f.url,
            uploadedBy: f.uploadedBy || 'admin'
          });
        }
      });

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/admin-save-step1-checklist`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId,
          memberIndex: (memberIndex !== undefined && memberIndex !== null && memberIndex >= 0) ? memberIndex : null,
          answers,
          comments,
          files: allFileRecords
        }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to save checklist');
      }

      const currentProgress = calculateProgress();
      alert(`Saved progress: ${currentProgress}/${totalQuestions} questions answered`);

      if (onSaved) onSaved();
    } catch (err) {
      console.error('Error saving checklist:', err);
      alert(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const getOptionStyles = (option, answer = "") => {
    const isSelected = answer.split(",").includes(option);
    if (!isSelected) return 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-600';
    switch (option) {
      case 'yes': return 'bg-green-500 text-white shadow-lg shadow-green-500/30';
      case 'no': return 'bg-red-500 text-white shadow-lg shadow-red-500/30';
      case 'na': return 'bg-gray-500 text-white shadow-lg shadow-gray-500/30';
      case 'comment': return 'bg-blue-500 text-white shadow-lg shadow-blue-500/30';
      default: return 'bg-gray-100 dark:bg-dark-700';
    }
  };

  if (!show || !modalRoot) return null;

  if (loading) {
    return createPortal(
      <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white dark:bg-dark-800 rounded-2xl p-8 shadow-2xl">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-blue-200 dark:border-blue-800 border-t-blue-500 rounded-full animate-spin mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading checklist...</p>
          </div>
        </div>
      </div>,
      modalRoot
    );
  }

  if (error) {
    return createPortal(
      <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white dark:bg-dark-800 rounded-2xl p-8 shadow-2xl max-w-md">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <FaTimes className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button onClick={onClose} className="px-6 py-2.5 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-all duration-200">Close</button>
          </div>
        </div>
      </div>,
      modalRoot
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/60 backdrop-blur-sm p-0 md:p-4 overflow-y-auto">
      <div className="bg-white dark:bg-dark-800 md:rounded-2xl rounded-none shadow-2xl w-full md:max-w-7xl h-full md:max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 dark:border-dark-700">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                {isSubmitted ? <FaEye className="text-blue-500" /> : null}
                Initial Information (Checklist)
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {userName} {memberIndex >= 0 ? `(Member ${memberIndex + 1})` : '(Primary)'} — Form {assignedForm}
                {isSubmitted && <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full">Submitted</span>}
              </p>
              {submittedAt && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Submitted on: <span className="font-semibold text-gray-600 dark:text-gray-300">{formatDateTime(submittedAt)}</span>
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-semibold text-sm">
                {calculateProgress()}/{totalQuestions}
              </span>
              <button onClick={onClose} className="p-2 rounded-xl bg-red-500 hover:bg-red-600 text-white transition-colors">
                <FaTimes className="w-4 h-4" />
              </button>
            </div>
          </div>
          {!isSubmitted && (
            <div className="p-2 md:p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30">
              <div className="space-y-1 text-xs md:text-sm text-amber-700 dark:text-amber-300">
                <p><span className="font-semibold">Admin Mode:</span> You can fill and save this checklist. Only the user can Confirm & Submit.</p>
              </div>
            </div>
          )}
          {isSubmitted && (
            <div className="p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30">
              <div className="space-y-1 text-sm text-green-700 dark:text-green-300">
                <p><span className="font-semibold">Read-Only:</span> This checklist has been submitted by the user. No changes can be made.</p>
              </div>
            </div>
          )}
        </div>

        {/* Questions Container */}
        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {Object.keys(formQuestions).map((questionTitle, index) => {
            const sectionLetter = String.fromCharCode(65 + index);
            const sectionProgress = calculateSectionProgress(questionTitle);
            const isExpanded = expanded[questionTitle];
            const isPartA = index === 0;
            const sectionIsPending = isPartA && formQuestions[questionTitle].some((_, subIdx) => {
              const key = `${questionTitle}-${subIdx}`;
              const ans = answers[key] || "";
              const qFiles = questionFiles[key] || [];
              const selectedOptions = ans.split(",");
              const showUpload = selectedOptions.includes('yes') || selectedOptions.includes('comment');
              const commentFilled = selectedOptions.includes('comment') ? (comments[key] && comments[key].trim() !== '') : true;
              return showUpload && (qFiles.length === 0 || !commentFilled);
            });

            return (
              <div key={index} className="rounded-2xl border border-gray-200 dark:border-dark-600 overflow-hidden shadow-sm">
                {/* Section Header */}
                <button
                  onClick={() => toggleQuestion(questionTitle)}
                  className="w-full flex items-center justify-between p-4 bg-white dark:bg-dark-700 hover:bg-gray-50 dark:hover:bg-dark-600 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white font-bold text-sm flex items-center justify-center shadow-md shadow-blue-500/30">
                      {sectionLetter}
                    </span>
                    <span className="font-semibold text-gray-900 dark:text-white text-left text-base">{questionTitle}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {sectionIsPending && (
                      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[10px] md:text-xs font-bold border border-amber-200 dark:border-amber-800/30 whitespace-nowrap">
                        <FaClock className="w-2.5 h-2.5" />
                        Pending
                      </span>
                    )}
                    <span className="px-3 py-1.5 rounded-full bg-gray-100 dark:bg-dark-600 text-sm font-semibold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-dark-500">
                      {sectionProgress}/{formQuestions[questionTitle].length}
                    </span>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${isExpanded ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-dark-600'}`}>
                      {isExpanded ? <FaChevronUp className="w-4 h-4 text-blue-500" /> : <FaChevronDown className="w-4 h-4 text-gray-500" />}
                    </div>
                  </div>
                </button>

                {/* Section Content */}
                {isExpanded && (
                  <div className="p-4 space-y-3 bg-gray-50 dark:bg-dark-800 border-t border-gray-200 dark:border-dark-600">
                    {formQuestions[questionTitle].map((subQuestion, subIndex) => {
                      const questionKey = `${questionTitle}-${subIndex}`;
                      const currentAnswer = answers[questionKey];
                      const qFiles = questionFiles[questionKey] || [];
                      const hasFile = qFiles.length > 0;
                        const selectedOptions = (currentAnswer || "").split(",");
                        const showUpload = isPartA && (selectedOptions.includes('yes') || selectedOptions.includes('comment')) && !isSubmitted;
                        const commentFilled = selectedOptions.includes('comment') ? (comments[questionKey] && comments[questionKey].trim() !== '') : true;
                        const isPending = isPartA && (selectedOptions.includes('yes') || selectedOptions.includes('comment')) && (!hasFile || !commentFilled);
                        const isCompleted = isPartA && (selectedOptions.includes('yes') || selectedOptions.includes('comment')) && hasFile && commentFilled;

                      return (
                        <div key={subIndex} className="p-4 rounded-xl bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 shadow-sm">
                          {/* Question row with status badge */}
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <p className="text-gray-900 dark:text-gray-200 leading-relaxed flex-1">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-semibold text-xs mr-2">{subIndex + 1}</span>
                              {subQuestion}
                            </p>
                            {/* Status Badge */}
                            {isPending && (
                              <span className="flex-shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[10px] md:text-xs font-bold border border-amber-200 dark:border-amber-800/30 whitespace-nowrap">
                                <FaClock className="w-2.5 h-2.5" />
                                Pending
                              </span>
                            )}
                            {isCompleted && (
                              <span className="flex-shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[10px] md:text-xs font-bold border border-green-200 dark:border-green-800/30 whitespace-nowrap">
                                <FaCheck className="w-2.5 h-2.5" />
                                Completed
                              </span>
                            )}
                          </div>

                          {/* Answer buttons */}
                          <div className="flex gap-1.5">
                            {["yes", "no", "na", "comment"].map((option) => (
                              <button
                                key={option}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAnswer(questionKey, option);
                                }}
                                disabled={isSubmitted}
                                className={`flex-1 md:flex-none md:w-24 px-1 py-1.5 md:py-1 rounded-lg font-bold text-xs transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${getOptionStyles(option, currentAnswer)}`}
                              >
                                {option.toUpperCase()}
                              </button>
                            ))}
                          </div>

                          {/* Comment box */}
                          {(currentAnswer || "").split(",").includes("comment") && (
                            <textarea
                              placeholder="Add a comment..."
                              value={comments[questionKey] || ""}
                              onChange={(e) => handleComment(questionKey, e.target.value)}
                              disabled={isSubmitted}
                              onClick={(e) => e.stopPropagation()}
                              className="mt-4 w-full p-3 rounded-xl bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-600 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 resize-none"
                              rows={2}
                            />
                          )}

                          {/* Part A: Upload section when "yes" selected (editable) */}
                          {showUpload && (
                            <div 
                              className={`mt-3 pt-3 border-t border-gray-100 dark:border-dark-600/50 transition-all duration-200 ${dragOverQuestion === questionKey ? 'bg-blue-50 dark:bg-blue-900/20 px-2 rounded-xl border-t-0 ring-2 ring-blue-400 ring-inset' : ''}`}
                              onDragOver={(e) => {
                                e.preventDefault();
                                if (!isSubmitted) setDragOverQuestion(questionKey);
                              }}
                              onDragLeave={() => setDragOverQuestion(null)}
                              onDrop={(e) => {
                                e.preventDefault();
                                setDragOverQuestion(null);
                                if (!isSubmitted) {
                                  const files = Array.from(e.dataTransfer.files);
                                  handleQuestionFileChange({ target: { files } }, questionKey);
                                }
                              }}
                            >
                              {qFiles.length < MAX_FILES_PER_QUESTION && (
                                <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-dashed border-blue-400 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 cursor-pointer text-xs font-semibold text-blue-600 dark:text-blue-400 transition-all duration-200">
                                  <input
                                    type="file"
                                    multiple
                                    className="hidden"
                                    onChange={(e) => handleQuestionFileChange(e, questionKey)}
                                    disabled={isSubmitted}
                                  />
                                  <FaUpload className="w-3 h-3" />
                                  + Upload File ({qFiles.length}/{MAX_FILES_PER_QUESTION})
                                </label>
                              )}
                              <div className="mt-1 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                                <span className="text-[10px] md:text-xs text-gray-400 dark:text-gray-500 font-medium whitespace-nowrap">
                                  Max {MAX_FILES_PER_QUESTION} files • 50 MB
                                </span>
                                <span className="hidden sm:inline text-gray-300 dark:text-gray-600">|</span>
                                <span className="text-[10px] md:text-xs text-blue-500/70 dark:text-blue-400/70 font-medium">
                                  {formatFileSize(getQuestionFilesTotalSize(questionKey))} used
                                </span>
                              </div>
                              {qFiles.length > 0 && (
                                <div className="mt-2 space-y-1.5">
                                  {qFiles.map((file, fi) => (
                                    <div key={fi} className="flex items-center justify-between py-1.5 px-2.5 rounded-lg bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-600">
                                      <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <FaFile className="w-3 h-3 text-blue-500 flex-shrink-0" />
                                        <div className="min-w-0 flex-1">
                                          <p className="text-xs text-gray-900 dark:text-white truncate font-medium" title={file.name}>{file.name}</p>
                                          <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-1 ml-2">
                                        <button
                                          onClick={() => setPreviewFile(file)}
                                          className="p-1 text-blue-500 hover:text-blue-600 transition-colors flex-shrink-0"
                                          title="Preview file"
                                        >
                                          <FaEye className="w-3 h-3" />
                                        </button>
                                        <button
                                          onClick={() => removeQuestionFile(questionKey, fi)}
                                          className="p-1 text-red-500 hover:text-red-600 transition-colors flex-shrink-0"
                                          title="Remove file"
                                        >
                                          <FaTrash className="w-3 h-3" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Separate view for question files that exist even if upload is not shown (e.g. submitted or other parts) */}
                              {!showUpload && qFiles.length > 0 && (
                                <div className="mt-2 space-y-1.5">
                                  {qFiles.map((file, fi) => (
                                    <div key={fi} className="flex items-center justify-between py-1.5 px-2.5 rounded-lg bg-gray-50/50 dark:bg-dark-800/50 border border-gray-100 dark:border-dark-700/50">
                                      <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <FaFile className={`w-3 h-3 ${file.uploadedBy === 'admin' ? 'text-blue-500' : 'text-gray-400'} flex-shrink-0`} />
                                        <div className="min-w-0 flex-1">
                                          <p className="text-xs text-gray-900 dark:text-white truncate font-medium" title={file.name}>{file.name}</p>
                                          <p className="text-[10px] text-gray-400">{formatFileSize(file.size)} • {file.uploadedBy === 'admin' ? 'Admin' : 'User'}</p>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-1 ml-2">
                                        <button
                                          onClick={() => setPreviewFile(file)}
                                          className="p-1 text-blue-500 hover:text-blue-600 transition-colors flex-shrink-0"
                                          title="Preview file"
                                        >
                                          <FaEye className="w-3 h-3" />
                                        </button>
                                        {!isSubmitted && (
                                          <button
                                            onClick={() => removeQuestionFile(questionKey, fi)}
                                            className="p-1 text-red-500 hover:text-red-600 transition-colors flex-shrink-0"
                                            title="Remove file"
                                          >
                                            <FaTrash className="w-3 h-3" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Bottom Upload Files Section */}
          {!isSubmitted && (
            <div className={`mt-4 rounded-2xl border border-gray-200 dark:border-dark-600 overflow-hidden shadow-sm transition-all duration-200 ${dragOverBottom ? 'ring-2 ring-blue-500 border-blue-500 bg-blue-50/50' : ''}`}>
              <div 
                className="p-4 bg-white dark:bg-dark-700"
                onDragOver={(e) => {
                  e.preventDefault();
                  if (!isSubmitted) setDragOverBottom(true);
                }}
                onDragLeave={() => setDragOverBottom(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOverBottom(false);
                  if (!isSubmitted) {
                    const files = Array.from(e.dataTransfer.files);
                    handleBottomFileChange({ target: { files } });
                  }
                }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white font-bold text-sm flex items-center justify-center shadow-md shadow-blue-500/30">
                    <FaUpload className="w-4 h-4" />
                  </span>
                  <div>
                    <span className="font-semibold text-gray-900 dark:text-white text-base">Upload Files</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Upload any additional documents here</p>
                  </div>
                </div>

                {/* Upload Button */}
                {bottomFiles.length < MAX_BOTTOM_FILES && (
                  <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-blue-400 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 cursor-pointer text-sm font-semibold text-blue-600 dark:text-blue-400 transition-all duration-200">
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleBottomFileChange}
                    />
                    <FaUpload className="w-3.5 h-3.5" />
                    + Upload Files ({bottomFiles.length}/{MAX_BOTTOM_FILES})
                  </label>
                )}

                {/* Uploaded files list */}
                {renderBottomFiles()}

                {/* Size info */}
                <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400 font-medium">
                  <span>Max {MAX_BOTTOM_FILES} files</span>
                  <span>•</span>
                  <span>Max 100 MB total ({formatFileSize(getBottomFilesTotalSize())} used)</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-gray-100 dark:border-dark-700 flex items-center justify-end gap-3 bg-white dark:bg-dark-800">
          {!isSubmitted && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-blue-500/30"
            >
              {saving ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaSave className="w-4 h-4" />}
              <span className="text-sm">{saving ? 'Saving...' : 'Save'}</span>
            </button>
          )}
        </div>
      </div>
      {previewFile && (
        <FilePreviewModal
          file={previewFile}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </div>,
    modalRoot
  );
};

export default Step1ChecklistModal;
